import { useCallback, useEffect, useState } from 'react';
import AuthLayout from '@/components/common/AuthLayout';
import LoginForm from '@/components/auth/LoginForm';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { loginEmail, loginGoogle } from '@/features/auth/authThunks';
import type { RootState } from '@/app/store';
import type { AppError } from '@/features/auth/types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { captureAffiliateFromSearch } from '@/lib/affiliateReferral';
import { reportSignupOrigin } from '@/lib/signupOrigin';
import { useCaptureSignupRedirect } from '@/hooks/useCaptureSignupRedirect';

export default function LoginPage() {
  const { t } = useTranslation('auth');
  const dispatch = useAppDispatch();
  const status = useAppSelector((s: RootState) => s.auth.status);
  const lastError = useAppSelector((s: RootState) => s.auth.error);
  const { search } = useLocation();
  useCaptureSignupRedirect();

  const [formError, setFormError] = useState<string | null>(null);

  const loading = status === 'loading';

  useEffect(() => {
    captureAffiliateFromSearch(search, Date.now());
  }, [search]);

  const handleSubmit = useCallback(
    async (data: { email: string; password: string }) => {
      setFormError(null);
      const result = await dispatch(
        loginEmail({
          email: data.email,
          password: data.password,
        }),
      );

      if (loginEmail.rejected.match(result)) {
        const err = result.payload as AppError | undefined;
        console.log('err', err);
        setFormError(err?.message ? t(err.message) : t('errors.generic'));
      }
    },
    [dispatch, t],
  );

  const handleGoogle = useCallback(async () => {
    setFormError(null);
    const result = await dispatch(loginGoogle({ mode: 'login' }));
    if (loginGoogle.fulfilled.match(result) && result.meta.isNewUser) {
      reportSignupOrigin({ method: 'google', page: 'login' });
    }
    if (loginGoogle.rejected.match(result)) {
      const err = result.payload;
      if (err?.code === 'cancelled') return;
      setFormError(err?.message ? t(err.message) : t('errors.generic'));
    }
  }, [dispatch, t]);

  return (
    <AuthLayout title={t('title')} background={'gradient'}>
      <LoginForm
        onSubmit={handleSubmit}
        onGoogle={handleGoogle}
        loading={loading}
        formError={formError ?? (lastError ? t(lastError) : null)}
      />
    </AuthLayout>
  );
}
